import { Editor, Extensions } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { ControlRef } from "./ControlRef";
import { InputControl, InputControlPropertyValues } from "./InputControl";
import { Toolbar } from "./Toolbar";
import { ToolbarButton, ToolbarButtonModeEnum } from "./ToolbarButton";
import { ToolbarSplitter } from "./ToolbarSplitter";

import "./BlockEditor.scss";

export class BlockEditor extends InputControl
{
    // #region Global Variables

    protected __editorUpdating: boolean = false;
    protected __settingToolbarButtonState: boolean = false;
    protected __settingToolbarButtonListState: boolean = false;
    protected _editor?: Editor;
    protected _editorContainerRef?: ElementRef<HTMLDivElement>;
    protected _textAreaContainerRef?: ElementRef<HTMLTextAreaElement>;
    protected _toolbarButtonBlockquote?: ControlRef<ToolbarButton>;
    protected _toolbarButtonLink?: ControlRef<ToolbarButton>;
    protected _toolbarButtonListBullets?: ControlRef<ToolbarButton>;
    protected _toolbarButtonListNumbers?: ControlRef<ToolbarButton>;
    protected _toolbarButtonBold?: ControlRef<ToolbarButton>;
    protected _toolbarButtonCode?: ControlRef<ToolbarButton>;
    protected _toolbarButtonItalic?: ControlRef<ToolbarButton>;
    protected _toolbarButtonUnderline?: ControlRef<ToolbarButton>;
    protected _toolbarButtonUnlink?: ControlRef<ToolbarButton>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<InputControlPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-block-editor");
    }

    // #endregion

    // #region Protected Methods

    protected _getExtensions(): Extensions
    {
        return [
            StarterKit.configure({
                link: {
                    openOnClick: false
                },
            }),
        ];
    }
    protected _updateToolbarLayout()
    {
        const
            codeView = this._toolbarButtonCode?.current?.state.get();

        this._toolbarButtonBlockquote?.current?.enabled.set(!codeView);
        this._toolbarButtonBold?.current?.enabled.set(!codeView);
        this._toolbarButtonItalic?.current?.enabled.set(!codeView);
        this._toolbarButtonUnderline?.current?.enabled.set(!codeView);
        this._toolbarButtonLink?.current?.enabled.set(!codeView);
        this._toolbarButtonListBullets?.current?.enabled.set(!codeView);
        this._toolbarButtonListNumbers?.current?.enabled.set(!codeView);
        this._toolbarButtonUnlink?.current?.enabled.set(!codeView);

        if (codeView)
        {
            this._editorContainerRef?.current.hide();
            this._textAreaContainerRef?.current.show();
        }
        else
        {
            this._editorContainerRef?.current.show();
            this._textAreaContainerRef?.current.hide();
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: InputControlPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.text.overrideGet(next =>
        {
            return this.value.get();
        });
        this.value.overrideSet((value, next) =>
        {
            next(value);

            if (!this.__editorUpdating)
            {
                this._editor?.commands.setContent(value);
                this._textAreaContainerRef?.current.val(StringHelper.htmlBeautify(this._editor?.getHTML()));
            }
        });

        this._editorContainerRef = new ElementRef();
        this._textAreaContainerRef = new ElementRef();
        this._toolbarButtonBlockquote = new ControlRef();
        this._toolbarButtonBold = new ControlRef();
        this._toolbarButtonCode = new ControlRef();
        this._toolbarButtonItalic = new ControlRef();
        this._toolbarButtonLink = new ControlRef();
        this._toolbarButtonListBullets = new ControlRef();
        this._toolbarButtonListNumbers = new ControlRef();
        this._toolbarButtonUnderline = new ControlRef();
        this._toolbarButtonUnlink = new ControlRef();
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("keydown keypress keyup", (evt: IJNativeEvent) => evt.stopPropagation());
        this._toolbarButtonBlockquote?.current?.on("stateChanged", { self: this }, this.onBlockquoteStateChanged);
        this._toolbarButtonBold?.current?.on("stateChanged", { self: this }, this.onBoldStateChanged);
        this._toolbarButtonCode?.current?.on("stateChanged", { self: this }, this.onCodeStateChanged);
        this._toolbarButtonItalic?.current?.on("stateChanged", { self: this }, this.onItalicStateChanged);
        this._toolbarButtonLink?.current?.on("click", { self: this }, this.onLinkClicked);
        this._toolbarButtonListBullets?.current?.on("stateChanged", { self: this, type: "bullets" }, this.onListStateChanged);
        this._toolbarButtonListNumbers?.current?.on("stateChanged", { self: this, type: "numbers" }, this.onListStateChanged);
        this._toolbarButtonUnderline?.current?.on("stateChanged", { self: this }, this.onUnderlineStateChanged);
        this._toolbarButtonUnlink?.current?.on("click", { self: this }, this.onUnlinkClicked);
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        const
            editorContainer = this._editorContainerRef?.current.get(0);

        Assert.isNotNullOrUndefined(editorContainer, "editorContainer");

        this._editor = new Editor({
            element: editorContainer,
            extensions: this._getExtensions(),
            onSelectionUpdate: ({ editor }) =>
            {
                this.__settingToolbarButtonState = true;
                this.onSelectionUpdate(editor);
                this.__settingToolbarButtonState = false;
            },
            onUpdate: ({ editor }) =>
            {
                this.__editorUpdating = true;
                this.value.set(editor.getHTML());
                this.__editorUpdating = false;
            },
        });

        this._textAreaContainerRef?.current.hide();
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[]): JsxFragment
    {
        return (
            <div>
                <Toolbar>
                    <ToolbarButton ref={this._toolbarButtonBold} icon="bold" mode={ToolbarButtonModeEnum.toggle} text="Bold" />
                    <ToolbarButton ref={this._toolbarButtonItalic} icon="italic" mode={ToolbarButtonModeEnum.toggle} text="Italic" />
                    <ToolbarButton ref={this._toolbarButtonUnderline} icon="underline" dividing={true} mode={ToolbarButtonModeEnum.toggle} text="Underline" />
                    <ToolbarButton ref={this._toolbarButtonBlockquote} icon="indent" mode={ToolbarButtonModeEnum.toggle} text="Blockquote" />
                    <ToolbarButton ref={this._toolbarButtonListBullets} icon="list ul" mode={ToolbarButtonModeEnum.toggle} text="Bullets" />
                    <ToolbarButton ref={this._toolbarButtonListNumbers} icon="list ol" dividing={true} mode={ToolbarButtonModeEnum.toggle} text="Numbering" />
                    <ToolbarButton ref={this._toolbarButtonLink} icon="linkify" text="Link" />
                    <ToolbarButton ref={this._toolbarButtonUnlink} dividing={true} enabled={false} icon="unlink" text="Unlink" />
                    <ToolbarButton ref={this._toolbarButtonCode} icon="code" mode={ToolbarButtonModeEnum.toggle} text="HTML" />
                    <ToolbarSplitter />
                </Toolbar>
                <div ref={this._editorContainerRef} class="editor-container"></div>
                <textarea ref={this._textAreaContainerRef}></textarea>
                {children}
            </div>
        );
    }

    // #endregion

    // #region Event Handlers

    protected onBlockquoteStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setBlockquote();
        else
            self._editor?.commands.unsetBlockquote();
    }
    protected onBoldStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setBold();
        else
            self._editor?.commands.unsetBold();
    }
    protected onCodeStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (change.currentValue)
            self._textAreaContainerRef?.current.val(StringHelper.htmlBeautify(self._editor?.getHTML()));
        else
            self._editor?.commands.setContent(self._textAreaContainerRef?.current.val() ?? StringHelper.empty);

        self._updateToolbarLayout();
    }
    protected onItalicStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setItalic();
        else
            self._editor?.commands.unsetItalic();
    }
    protected onLinkClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this,
            previousUrl = self._editor?.getAttributes("link").href,
            url = window.prompt("URL", previousUrl);

        if (url === undefined || url === null)
            return;

        if (!url)
        {
            self._editor?.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }

        try
        {
            self._editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        }
        catch (e: any)
        {
            alert(e.message);
        }
    }
    protected onListStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (self.__settingToolbarButtonListState)
            return;

        Assert.isNotNullOrUndefined(self._editor, "self._editor");

        switch (evt.data.type)
        {
            case "bullets":
                {
                    if (change.currentValue !== !!self._editor.isActive("bulletList"))
                        self._editor.chain().focus().toggleBulletList().run();

                    self.__settingToolbarButtonListState = true;
                    self._toolbarButtonListNumbers?.current?.state.set(false);
                    self.__settingToolbarButtonListState = false;

                    break;
                }
            case "numbers":
                {
                    if (change.currentValue !== !!self._editor.isActive("orderedList"))
                        self._editor.chain().focus().toggleOrderedList().run();

                    self.__settingToolbarButtonListState = true;
                    self._toolbarButtonListBullets?.current?.state.set(false);
                    self.__settingToolbarButtonListState = false;

                    break;
                }
        }
    }
    protected onSelectionUpdate(editor: Editor)
    {
        this._toolbarButtonBlockquote?.current?.state.set(editor.isActive("blockquote") ?? false);
        this._toolbarButtonBold?.current?.state.set(editor.isActive("bold") ?? false);
        this._toolbarButtonItalic?.current?.state.set(editor.isActive("italic") ?? false);
        this._toolbarButtonListBullets?.current?.state.set(editor.isActive("bulletList") ?? false);
        this._toolbarButtonListNumbers?.current?.state.set(editor.isActive("orderedList") ?? false);
        this._toolbarButtonUnderline?.current?.state.set(editor.isActive("underline") ?? false);
        this._toolbarButtonUnlink?.current?.enabled.set(editor.isActive("link") ?? false);
    }
    protected onUnderlineStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setUnderline();
        else
            self._editor?.commands.unsetUnderline();
    }
    protected onUnlinkClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    }

    // #endregion
}